import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive } from "lucide-react";
import { Badge, Button, Card, FormField, Input } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { PageHeader } from "@/components/layout/PageHeader";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { useDateFormatter } from "@/utils/datetime";
import { formatCurrency } from "@/components/deals/dealFormat";
import { escrowApi, type EscrowListParams } from "@/api/escrow";
import { extractErrorMessage } from "@/api/client";
import { EscrowWebhookDiagnosticsCard } from "@/components/escrow/EscrowWebhookDiagnosticsCard";
import { EscrowLiveBadge } from "@/components/escrow/EscrowLiveBadge";
import {
  ESCROW_PERMISSIONS,
  ESCROW_STATUS_LABEL,
  escrowStatusBadge,
  type EscrowStatus,
  type EscrowTransactionDto,
} from "@/types/escrow";

const STATUS_OPTIONS: EscrowStatus[] = [
  "PendingAdminApproval",
  "ApprovedSentToEscrow",
  "Active",
  "Closed",
  "Cancelled",
  "Archived",
];

export function EscrowListPage() {
  const { hasPermission } = useAuth();
  const toast = useToast();
  const fmt = useDateFormatter();
  const queryClient = useQueryClient();
  const canApprove = hasPermission(ESCROW_PERMISSIONS.Approve);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"All" | EscrowStatus>("All");
  const [showArchived, setShowArchived] = useState(false);
  const [archiveFor, setArchiveFor] = useState<EscrowTransactionDto | null>(null);
  const [showDiagnostics, setShowDiagnostics] = useState(false);

  const params: EscrowListParams = useMemo(
    () => ({
      pageSize: 200,
      q: search || undefined,
      status: statusFilter === "All" ? undefined : statusFilter,
      includeArchived: showArchived,
    }),
    [search, statusFilter, showArchived],
  );

  const query = useQuery({
    queryKey: ["escrow", "list", params],
    queryFn: () => escrowApi.list(params),
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => escrowApi.archive(id),
    onSuccess: () => {
      toast.success("Escrow transaction archived.");
      queryClient.invalidateQueries({ queryKey: ["escrow"] });
      setArchiveFor(null);
    },
    onError: (err) => toast.error(extractErrorMessage(err)),
  });

  const rows = query.data?.data ?? [];
  // The resolved Escrow environment is process-global, so any row reflects it.
  const isLive = rows.some((r) => r.isLive);

  const columns: Column<EscrowTransactionDto>[] = [
    {
      key: "deal",
      header: "Deal",
      sortable: true,
      sortAccessor: (x) => x.dealNumber ?? "",
      render: (x) => (
        <Link to={`/pipeline/${x.dealId}`} className="font-semibold hover:text-gold-shadow">
          {x.dealNumber ?? x.dealId.slice(0, 8)}
          {x.businessName ? (
            <span className="block text-caption text-gray-mid">{x.businessName}</span>
          ) : null}
        </Link>
      ),
    },
    {
      key: "amount",
      header: "Amount",
      sortable: true,
      sortAccessor: (x) => x.amount,
      render: (x) => `${formatCurrency(x.amount)} ${x.currency}`,
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      sortAccessor: (x) => x.status,
      render: (x) => <Badge status={escrowStatusBadge(x.status)}>{ESCROW_STATUS_LABEL[x.status]}</Badge>,
    },
    {
      key: "escrowComId",
      header: "Escrow.com ID",
      render: (x) => x.escrowComTransactionId ?? "—",
    },
    {
      key: "draftedAt",
      header: "Drafted",
      sortable: true,
      sortAccessor: (x) => x.draftedAt ?? "",
      render: (x) => (x.draftedAt ? fmt.date(x.draftedAt) : "—"),
    },
    {
      key: "approvedAt",
      header: "Approved",
      render: (x) => (x.approvedAt ? fmt.date(x.approvedAt) : "—"),
    },
    {
      key: "lastSyncedAt",
      header: "Last synced",
      render: (x) => (x.lastSyncedAt ? fmt.dateTime(x.lastSyncedAt) : "—"),
    },
    {
      key: "actions",
      header: "",
      className: "text-right",
      render: (x) => (
        <div className="flex items-center justify-end gap-space-3">
          <Link
            to={`/escrow/${x.id}`}
            className="text-caption text-gold-shadow hover:text-gold-highlight"
          >
            View
          </Link>
          {canApprove && x.status !== "Archived" && (
            <Button size="sm" variant="tertiary" onClick={() => setArchiveFor(x)}>
              <Archive size={14} aria-hidden /> Archive
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-space-4">
      <PageHeader
        eyebrow="Tools"
        title="Escrow"
        description="API-driven Escrow.com transactions tied to deals."
        actions={
          <div className="flex items-center gap-space-2">
            <EscrowLiveBadge isLive={isLive} />
            {canApprove && (
              <Button variant="tertiary" size="sm" onClick={() => setShowDiagnostics((v) => !v)}>
                {showDiagnostics ? "Hide diagnostics" : "Webhook diagnostics"}
              </Button>
            )}
          </div>
        }
      />

      {canApprove && showDiagnostics && <EscrowWebhookDiagnosticsCard />}

      <Card>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-space-3">
          <FormField label="Search" htmlFor="escrow-search">
            <Input
              id="escrow-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Description, Escrow.com ID, deal #"
            />
          </FormField>
          <FormField label="Status" htmlFor="escrow-status">
            <Select
              id="escrow-status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as "All" | EscrowStatus)}
            >
              <option value="All">All statuses</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {ESCROW_STATUS_LABEL[s]}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField label="Archived" htmlFor="escrow-archived">
            <label className="flex items-center gap-space-2 h-10 text-small text-onyx">
              <input
                id="escrow-archived"
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
              Show archived
            </label>
          </FormField>
        </div>
      </Card>

      <DataTable
        columns={columns}
        rows={rows}
        loading={query.isLoading}
        error={query.isError ? "Could not load escrow transactions." : null}
        rowKey={(x) => x.id}
        sortable
        pagination="auto"
        emptyMessage="No escrow transactions yet."
      />

      <Modal
        open={!!archiveFor}
        onClose={() => setArchiveFor(null)}
        title="Archive escrow transaction"
        description={archiveFor?.dealNumber ?? undefined}
        footer={
          <>
            <Button variant="tertiary" onClick={() => setArchiveFor(null)} disabled={archiveMutation.isPending}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => archiveFor && archiveMutation.mutate(archiveFor.id)}
              loading={archiveMutation.isPending}
            >
              Archive
            </Button>
          </>
        }
      >
        <p className="text-small text-gray-mid">
          Archived transactions are hidden from the default list — find them again with
          the “Show archived” filter. This does not affect the transaction on Escrow.com.
        </p>
      </Modal>
    </div>
  );
}
